import { Controller } from "@hotwired/stimulus"

// Voice call mode, as a small floating call bar (shared/_jane_call_dock) that
// sits over the app instead of replacing it, so the user keeps working while
// they talk. It connects a WebRTC session to the OpenAI Realtime API (server mints an ephemeral token
// via /assistant/voice_token), greets the user by name, and holds a continuous
// conversation with tool access (bridged through /assistant/voice_tool).
// The session's input transcription, tools and instructions are all
// configured server-side at mint time — the client only reacts to events.
// Finalized transcript lines are persisted via /assistant/voice_turn so the
// call shows up in Messages history like a normal chat.
//
// Cost guardrails: calls hard-cap at MAX_CALL_MS, idle calls (no speech
// either way) end after IDLE_LIMIT_MS, and a mid-call credit exhaustion
// (flagged by /assistant/voice_turn) winds the call down gracefully.
const MAX_CALL_MS = 30 * 60 * 1000
const IDLE_LIMIT_MS = 3 * 60 * 1000
const WATCHDOG_INTERVAL_MS = 15 * 1000

export default class extends Controller {
    static targets = ["overlay", "status", "timer", "transcript", "orb", "name", "avatar"]
    static values = { firstName: String, conversationId: String }

    // The call bar is data-turbo-permanent: Turbo MOVES this element into each
    // new page rather than rendering a fresh one, and Stimulus answers a move
    // with disconnect() + connect() on this same instance. So connect() must
    // not reset a live call, and disconnect() must not end one — only a bar
    // that is really gone (a page without the layout) hangs up.
    connect() {
        if (this._initialized) return
        this._initialized = true
        this._attempt = 0
        // A face that fails to load (an expired signed URL) falls back to the orb.
        if (this.hasAvatarTarget) this.avatarTarget.addEventListener("error", () => { this.avatarTarget.hidden = true })
        this._connectedAt = null
        this._timerId = null
        this._state = "idle"
        this._pc = null
        this._dc = null
        this._micStream = null
        this._remoteAudio = null
        this._conversationId = null
        this._greeted = false
        this._sawAudioTranscript = false
        this._currentUserItemId = null
        this._micBlocked = false
        this._persistedAny = false
        this._persistedResponseIds = new Set()
        this._greetingResponseId = null
        this._awaitingGreeting = false
        this._handledToolCallIds = new Set()
        this._lastActivityAt = null
        this._watchdogId = null
        this._endingSoon = false
    }

    disconnect() {
        setTimeout(() => {
            if (!this.element.isConnected) this._stop()
        }, 0)
    }

    // `detail.conversationId`: the thread the call belongs to (the composer's
    // or the Jane panel's). A second open while a call is live just reveals it.
    open(event) {
        if (!this.hasOverlayTarget) return
        if (this._state !== "idle") {
            this.overlayTarget.classList.add("is-open")
            return
        }
        if (event?.detail?.name) this._showCaller(event.detail.name, event.detail.avatar)
        this._requestedProfileSlug = event?.detail?.profileSlug || null
        this._requestedTwinChatId = event?.detail?.twinChatId || null
        this._twinChatId = null
        this._requestedConversationId = event?.detail?.conversationId || null
        this.overlayTarget.classList.add("is-open")
        this.overlayTarget.classList.remove("is-expanded")
        this._setState("connecting")
        this._start()
    }

    // Hanging up leaves the user where they are. The call's lines were saved
    // to its thread as they happened; if that thread is on screen, refresh it
    // so they appear.
    close() {
        if (!this.hasOverlayTarget) return
        const conversationId = this._conversationId
        const hadTurns = this._persistedAny
        const profileSlug = this._requestedProfileSlug
        const twinChatId = this._twinChatId
        this.overlayTarget.classList.remove("is-open", "is-muted", "is-expanded")
        this._setState("idle")
        this._stop()
        if (profileSlug && twinChatId) {
            window.dispatchEvent(new CustomEvent("profile-voice:ended", { detail: { profileSlug, twinChatId } }))
        }
        if (hadTurns && conversationId && window.location.pathname === `/messages/${conversationId}` && window.Turbo) {
            window.Turbo.visit(window.location.href, { action: "replace" })
        }
    }

    toggleTranscript() {
        if (!this.hasOverlayTarget) return
        this.overlayTarget.classList.toggle("is-expanded")
        this._scrollTranscriptToBottom()
    }

    toggleMute() {
        if (!this.hasOverlayTarget) return
        if (this._micBlocked) return
        this.overlayTarget.classList.toggle("is-muted")
        const muted = this.overlayTarget.classList.contains("is-muted")
        if (this._micStream) {
            this._micStream.getAudioTracks().forEach((t) => {
                t.enabled = !muted
            })
        }
    }

    _setState(next) {
        this._state = next
        // The floating avatar (jane_avatar_controller) listens, talks, thinks along.
        window.dispatchEvent(new CustomEvent("jane:voice", { detail: { state: next } }))
        const labels = {
            idle: "",
            connecting: "Connecting…",
            listening: "Listening…",
            thinking: "Thinking…",
            speaking: "Speaking…",
            error: "Call failed"
        }
        if (this.hasStatusTarget && labels[next] !== undefined) {
            this.statusTarget.textContent = labels[next]
        }
        if (this.hasOrbTarget) {
            this.orbTarget.classList.remove("is-listening", "is-thinking", "is-speaking")
            if (next === "listening") this.orbTarget.classList.add("is-listening")
            if (next === "thinking") this.orbTarget.classList.add("is-thinking")
            if (next === "speaking") this.orbTarget.classList.add("is-speaking")
        }

        if (next === "idle") {
            this._stopTimer()
            return
        }
        if (next === "connecting") {
            this._stopTimer()
            this._connectedAt = null
            this._setTimerText("00:00")
            this._clearTranscript()
            return
        }
        if (next === "listening" && !this._timerId) {
            this._connectedAt = this._connectedAt || Date.now()
            this._startTimer()
        }
    }

    _clearTranscript() {
        if (!this.hasTranscriptTarget) return
        this.transcriptTarget.innerHTML = ""
    }

    _startTimer() {
        this._stopTimer()
        this._timerId = setInterval(() => {
            const s = Math.max(0, Math.floor((Date.now() - this._connectedAt) / 1000))
            const mm = String(Math.floor(s / 60)).padStart(2, "0")
            const ss = String(s % 60).padStart(2, "0")
            this._setTimerText(`${mm}:${ss}`)
        }, 500)
    }

    _stopTimer() {
        if (this._timerId) clearInterval(this._timerId)
        this._timerId = null
    }

    _setTimerText(text) {
        if (!this.hasTimerTarget) return
        this.timerTarget.textContent = text
    }

    async _start() {
        const attempt = ++this._attempt
        try {
            const { ephemeralKey, sessionToken, conversationId, history } = await this._fetchEphemeralKey(attempt)
            if (attempt !== this._attempt) return
            this._voiceSessionToken = sessionToken
            this._history = history
            this._historySeeded = false
            this._conversationId = conversationId
            this._greeted = false
            this._sawAudioTranscript = false
            this._micBlocked = false
            this._persistedAny = false
            this._persistedResponseIds = new Set()
            this._greetingResponseId = null
            this._awaitingGreeting = false
            this._handledToolCallIds = new Set()
            this._lastActivityAt = Date.now()
            this._endingSoon = false
            this._startWatchdog()

            const pc = new RTCPeerConnection()
            this._pc = pc

            // Remote audio playback
            this._remoteAudio = new Audio()
            this._remoteAudio.autoplay = true
            pc.ontrack = (e) => {
                this._remoteAudio.srcObject = e.streams[0]
            }

            // Local microphone. If blocked, still connect receive-only so the
            // user hears the assistant and sees a clear message.
            try {
                const micStream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        echoCancellation: true,
                        noiseSuppression: true,
                        autoGainControl: true
                    }
                })
                if (attempt !== this._attempt) { micStream.getTracks().forEach(t => t.stop()); return }
                this._micStream = micStream
                this._micStream.getTracks().forEach((t) => pc.addTrack(t, this._micStream))
            } catch (micErr) {
                if (attempt !== this._attempt) return
                this._micBlocked = true
                pc.addTransceiver("audio", { direction: "recvonly" })
                this._appendSystem("Microphone is blocked - the assistant can speak but can't hear you. Enable mic access in your browser settings, then start a new call.")
            }

            // Data channel for events (tools + transcripts)
            const dc = pc.createDataChannel("oai-events")
            this._dc = dc
            dc.addEventListener("open", () => {
                this._setState("listening")
                // The session is fully configured at mint time; greet as soon
                // as the channel is live (session.created also triggers this,
                // whichever arrives first).
                clearTimeout(this._greetFallback)
                this._greetFallback = setTimeout(() => {
                    if (!this._greeted) this._sendGreeting()
                }, 1200)
            })
            dc.addEventListener("message", (e) => this._handleEvent(e))
            dc.addEventListener("close", () => {
                if (attempt === this._attempt && sessionToken) this._checkVoiceSessionStatus()
            })

            const offer = await pc.createOffer()
            await pc.setLocalDescription(offer)
            if (attempt !== this._attempt) return

            let answer
            if (sessionToken) {
                const csrf = document.querySelector("meta[name='csrf-token']")?.content
                const response = await fetch("/assistant/voice_connect", {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Accept: "application/json", ...(csrf ? { "X-CSRF-Token": csrf } : {}) },
                    body: JSON.stringify({ session_token: sessionToken, sdp: offer.sdp })
                })
                const data = await response.json()
                if (!response.ok || !data.ok) throw new Error(data.error || "The call could not be connected")
                answer = { type: "answer", sdp: data.sdp }
            } else {
                const sdpResp = await fetch("https://api.openai.com/v1/realtime/calls", {
                    method: "POST",
                    body: offer.sdp,
                    headers: {
                        Authorization: `Bearer ${ephemeralKey}`,
                        "Content-Type": "application/sdp"
                    }
                })

                answer = { type: "answer", sdp: await sdpResp.text() }
            }
            if (attempt !== this._attempt) return
            await pc.setRemoteDescription(answer)
        } catch (e) {
            if (attempt !== this._attempt) return
            this._appendSystem(`Voice error: ${e?.message || e}`)
            this._stop()
            this._setState("error")
        }
    }

    _stop() {
        this._attempt++
        if (this._voiceSessionToken) {
            const csrf = document.querySelector("meta[name='csrf-token']")?.content
            fetch("/assistant/voice_end", {
                method: "POST", keepalive: true,
                headers: { "Content-Type": "application/json", ...(csrf ? { "X-CSRF-Token": csrf } : {}) },
                body: JSON.stringify({ session_token: this._voiceSessionToken })
            }).catch(() => {})
            this._voiceSessionToken = null
        }
        try {
            if (this._dc) this._dc.close()
        } catch (e) {}
        try {
            if (this._pc) this._pc.close()
        } catch (e) {}
        try {
            if (this._micStream) this._micStream.getTracks().forEach((t) => t.stop())
        } catch (e) {}
        try {
            if (this._remoteAudio) {
                this._remoteAudio.pause()
                this._remoteAudio.srcObject = null
            }
        } catch (e) {}
        try {
            clearTimeout(this._greetFallback)
        } catch (e) {}
        this._stopWatchdog()
        this._dc = null
        this._pc = null
        this._micStream = null
        this._remoteAudio = null
        this._conversationId = null
        this._greeted = false
        this._sawAudioTranscript = false
        this._micBlocked = false
        this._persistedAny = false
    }

    async _fetchEphemeralKey(attempt) {
        const token = document.querySelector("meta[name='csrf-token']")?.content
        const body = {}
        const conversationId = this._requestedConversationId || (this.hasConversationIdValue ? this.conversationIdValue : null)
        if (this._requestedProfileSlug) {
            body.profile_slug = this._requestedProfileSlug
            if (this._requestedTwinChatId) body.twin_chat_id = this._requestedTwinChatId
        } else if (conversationId) body.conversation_id = conversationId
        const r = await fetch("/assistant/voice_token", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                ...(token ? { "X-CSRF-Token": token } : {})
            },
            body: JSON.stringify(body)
        })
        const data = await r.json()
        if (attempt !== this._attempt) return {}
        if (!data || !data.ok) throw new Error(data?.error || "Failed to create voice session")
        this._twinChatId = data.twin_chat_id
        this._showCaller(data.assistant_name, data.assistant_avatar)
        return { ephemeralKey: data.value, sessionToken: data.session_token, conversationId: data.conversation_id, history: data.history || [] }
    }

    // Who is on the line: the persona of the thread the call belongs to.
    _showCaller(name, avatar) {
        if (name && this.hasNameTarget) this.nameTarget.textContent = name
        if (this.hasAvatarTarget) {
            if (avatar) {
                this.avatarTarget.src = avatar
                this.avatarTarget.hidden = false
            } else {
                this.avatarTarget.hidden = true
                this.avatarTarget.removeAttribute("src")
            }
        }
    }

    _send(json) {
        if (!this._dc || this._dc.readyState !== "open") return
        this._dc.send(JSON.stringify(json))
    }

    _sendGreeting() {
        if (!this._dc || this._dc.readyState !== "open") return
        if (!this._historySeeded) {
            for (const turn of this._history || []) {
                this._send({ type: "conversation.item.create", item: {
                    type: "message", role: turn.role,
                    content: [{ type: turn.role === "user" ? "input_text" : "output_text", text: turn.content }]
                } })
            }
            this._historySeeded = true
        }
        this._greeted = true
        // The greeting is a scripted hello, not conversation: it's spoken and
        // shown live, but never saved to the thread - every call used to leave
        // another "Hi Pieter, how can I help?" there, even one hung up at once.
        this._awaitingGreeting = true
        const name = (this.hasFirstNameValue ? this.firstNameValue : "").trim()
        const greeting = name ? `Hi ${name}, how can I help?` : "Hi, how can I help?"
        this._send({
            type: "response.create",
            response: {
                instructions: `Say exactly this greeting in a warm tone, in English, and nothing else: "${greeting}"`
            }
        })
    }

    async _handleEvent(e) {
        let ev = null
        try {
            ev = JSON.parse(e.data)
        } catch {
            return
        }

        const t = ev?.type?.toString() || ""

        // Diagnostic log of the event stream (skip high-frequency deltas).
        if (!t.endsWith(".delta")) console.debug("voice.event", t, ev)

        if (t === "error") {
            const msg = ev?.error?.message || "Unknown realtime error"
            console.error("voice.error", ev)
            this._appendSystem(`Voice error: ${msg}`)
            return
        }

        if (t === "session.created" || t === "session.updated") {
            // Exposed for diagnostics: confirms transcription + tools are
            // active on the live session.
            window.__voiceSessionAck = ev.session || null
            console.debug("voice.session_ack", { transcription: ev.session?.audio?.input?.transcription, tools: (ev.session?.tools || []).map((x) => x.name) })
            if (!this._greeted) this._sendGreeting()
            return
        }

        // Turn-taking status for the orb + status line
        if (t === "input_audio_buffer.speech_started") {
            this._lastActivityAt = Date.now()
            this._setState("listening")
            return
        }
        if (t === "input_audio_buffer.speech_stopped" || t === "response.created") {
            if (t === "response.created" && this._awaitingGreeting) {
                this._greetingResponseId = ev.response?.id || null
                this._awaitingGreeting = false
            }
            this._lastActivityAt = Date.now()
            this._setState("thinking")
            return
        }
        if (t === "output_audio_buffer.started") {
            this._lastActivityAt = Date.now()
            this._setState("speaking")
            return
        }
        if (t === "output_audio_buffer.stopped") {
            this._setState("listening")
            return
        }

        // Show user transcripts (what you're saying)
        if (t === "conversation.item.input_audio_transcription.delta" && typeof ev.delta === "string") {
            this._appendUserDelta(ev.item_id || null, ev.delta)
            return
        }
        if (t === "conversation.item.input_audio_transcription.completed" && typeof ev.transcript === "string") {
            this._finalizeUser(ev.item_id || null, ev.transcript)
            this._persistTurn("user", ev.transcript)
            return
        }
        if (t === "conversation.item.input_audio_transcription.failed") {
            console.error("voice.transcription_failed", ev)
            return
        }

        // Show assistant transcripts (text + audio transcript)
        if (t === "response.output_audio_transcript.delta" && typeof ev.delta === "string") {
            this._sawAudioTranscript = true
            this._setState("speaking")
            this._appendAssistantDelta(ev.delta)
            return
        }
        if (t === "response.output_audio_transcript.done" && typeof ev.transcript === "string") {
            this._finalizeCurrent()
            this._persistAssistantOnce(ev.response_id || ev.item_id, ev.transcript)
            return
        }
        if (!this._sawAudioTranscript && t === "response.output_text.delta" && typeof ev.delta === "string") {
            this._appendAssistantDelta(ev.delta)
            return
        }
        if (!this._sawAudioTranscript && t === "response.output_text.done" && typeof ev.text === "string") {
            this._finalizeCurrent()
            this._persistAssistantOnce(ev.response_id || ev.item_id, ev.text)
            return
        }

        // Tool calling. response.output_item.done carries the completed
        // function_call item (name + call_id + arguments together); the
        // response.done output array is a belt-and-braces fallback. A dedup
        // set makes handling idempotent across the overlapping events.
        if (t === "response.output_item.done" && ev.item?.type === "function_call") {
            await this._handleFunctionCall(ev.item)
            return
        }
        if (t === "response.done") {
            const calls = (ev.response?.output || []).filter((item) => item?.type === "function_call")
            for (const item of calls) {
                await this._handleFunctionCall(item)
            }
            // No function calls pending and no audio playing: back to listening.
            if (!calls.length && this._state === "thinking") this._setState("listening")
            return
        }

        if (t.endsWith(".done")) {
            this._finalizeCurrent()
        }
    }

    async _handleFunctionCall(item) {
        const name = item?.name
        const callId = item?.call_id
        const argsStr = item?.arguments || "{}"
        if (!name || !callId) return
        if (this._handledToolCallIds.has(callId)) return
        this._handledToolCallIds.add(callId)
        console.debug("voice.tool_call", { name, callId, argsStr })
        this._setState("thinking")
        await this._runTool(callId, name, argsStr)
    }

    _persistAssistantOnce(responseId, text) {
        if (responseId && responseId === this._greetingResponseId) return
        const key = (responseId || text || "").toString()
        if (this._persistedResponseIds.has(key)) return
        this._persistedResponseIds.add(key)
        this._persistTurn("assistant", text)
    }

    async _persistTurn(role, content) {
        const text = (content || "").trim()
        if (!text || !this._conversationId) return
        this._persistedAny = true
        const token = document.querySelector("meta[name='csrf-token']")?.content
        try {
            const resp = await fetch("/assistant/voice_turn", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    ...(token ? { "X-CSRF-Token": token } : {})
                },
                body: JSON.stringify({ conversation_id: this._conversationId, session_token: this._voiceSessionToken, role, content: text })
            })
            const data = await resp.json().catch(() => null)
            if (data && data.credits_exhausted) {
                this._endCall("You're out of Jane credits - ending the call here. Earn more via milestones and invites.")
            }
        } catch (e) {}
    }

    _startWatchdog() {
        this._stopWatchdog()
        this._watchdogId = setInterval(() => {
            if (this._state === "idle" || this._state === "error") return
            const now = Date.now()
            this._checkVoiceSessionStatus()
            if (this._connectedAt && now - this._connectedAt > MAX_CALL_MS) {
                this._endCall("We've hit the call length limit - let's pick this up in a fresh call.")
                return
            }
            if (this._lastActivityAt && now - this._lastActivityAt > IDLE_LIMIT_MS) {
                this._endCall("Ending the call since things went quiet - start a new one any time.")
            }
        }, WATCHDOG_INTERVAL_MS)
    }

    async _checkVoiceSessionStatus() {
        const sessionToken = this._voiceSessionToken
        if (!sessionToken || this._checkingVoiceStatus) return
        this._checkingVoiceStatus = true
        try {
            const response = await fetch(`/assistant/voice_status?session_token=${encodeURIComponent(sessionToken)}`, { headers: { Accept: "application/json" } })
            const data = await response.json()
            if (sessionToken !== this._voiceSessionToken) return
            if (data.ended) this._endCall(data.credits_exhausted ? "Add credits to keep using Jane's AI. This call has ended." : "This call has ended. You can start a new one any time.")
        } catch (_) {
        } finally {
            this._checkingVoiceStatus = false
        }
    }

    _stopWatchdog() {
        if (this._watchdogId) clearInterval(this._watchdogId)
        this._watchdogId = null
    }

    // Graceful guardrail exit: show why, give the ear a beat, then hang up.
    _endCall(reason) {
        if (this._endingSoon) return
        this._endingSoon = true
        this._appendSystem(reason)
        setTimeout(() => this.close(), 2500)
    }

    _appendSystem(text) {
        if (!this.hasTranscriptTarget) return
        const row = document.createElement("div")
        row.className = "voice-call-row voice-call-row--system"
        row.textContent = text
        this.transcriptTarget.appendChild(row)
        // A problem (blocked mic, failed call) must be seen, not folded away.
        if (this.hasOverlayTarget) this.overlayTarget.classList.add("is-expanded")
        this._scrollTranscriptToBottom()
    }

    _appendAssistantDelta(delta) {
        if (!this.hasTranscriptTarget) return
        const existing = this.transcriptTarget.querySelector("[data-assistant-current='true']")
        if (existing) {
            existing.textContent += delta
            this._scrollTranscriptToBottom()
            return
        }
        const row = document.createElement("div")
        row.className = "voice-call-row voice-call-row--assistant"
        row.setAttribute("data-assistant-current", "true")
        row.textContent = delta
        this.transcriptTarget.appendChild(row)
        this._scrollTranscriptToBottom()
    }

    _finalizeCurrent() {
        if (!this.hasTranscriptTarget) return
        const existing = this.transcriptTarget.querySelector("[data-assistant-current='true']")
        if (existing) existing.removeAttribute("data-assistant-current")
    }

    _appendUserDelta(itemId, delta) {
        if (!this.hasTranscriptTarget) return
        const effectiveId = itemId || "unknown"
        const existing = this.transcriptTarget.querySelector("[data-user-current='true']")
        if (existing && this._currentUserItemId === effectiveId) {
            existing.textContent += delta
            this._scrollTranscriptToBottom()
            return
        }

        if (existing) existing.removeAttribute("data-user-current")
        this._currentUserItemId = effectiveId

        const row = document.createElement("div")
        row.className = "voice-call-row voice-call-row--user"
        row.setAttribute("data-user-current", "true")
        row.textContent = delta
        this.transcriptTarget.appendChild(row)
        this._scrollTranscriptToBottom()
    }

    _finalizeUser(itemId, transcript) {
        if (!this.hasTranscriptTarget) return
        const effectiveId = itemId || "unknown"
        const existing = this.transcriptTarget.querySelector("[data-user-current='true']")

        if (existing && this._currentUserItemId === effectiveId) {
            existing.textContent = transcript
            existing.removeAttribute("data-user-current")
            this._currentUserItemId = null
            this._scrollTranscriptToBottom()
            return
        }

        const row = document.createElement("div")
        row.className = "voice-call-row voice-call-row--user"
        row.textContent = transcript
        this.transcriptTarget.appendChild(row)
        this._scrollTranscriptToBottom()
    }

    _scrollTranscriptToBottom() {
        if (!this.hasTranscriptTarget) return
        try {
            this.transcriptTarget.scrollTop = this.transcriptTarget.scrollHeight
        } catch (e) {}
    }

    async _runTool(callId, name, argsStr) {
        let args = {}
        try {
            args = JSON.parse(argsStr)
        } catch (e) {
            args = {}
        }

        const token = document.querySelector("meta[name='csrf-token']")?.content
        const channel = this._dc
        let output = null
        try {
            const resp = await fetch("/assistant/voice_tool", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    ...(token ? { "X-CSRF-Token": token } : {})
                },
                body: JSON.stringify({
                    conversation_id: this._conversationId,
                    session_token: this._voiceSessionToken,
                    call_id: callId,
                    name,
                    arguments: args
                })
            })
            let data = await resp.json()
            const deadline = Date.now() + 5 * 60 * 1000
            while (data?.pending) {
                if (this._dc !== channel || channel?.readyState !== "open") return
                if (Date.now() >= deadline) throw new Error("The lookup is still running. Please check the app shortly.")
                this._lastActivityAt = Date.now()
                await new Promise((resolve) => setTimeout(resolve, 1000))
                const status = await fetch(`/assistant/voice_tool_status?invocation_id=${data.invocation_id}`, { headers: { Accept: "application/json" } })
                if (!status.ok) throw new Error("Couldn't retrieve the lookup result")
                data = { ...await status.json(), invocation_id: data.invocation_id }
            }
            output = data && data.ok ? data.output : { error: data?.error || "tool failed" }
        } catch (e) {
            output = { error: e?.message || "tool bridge failed" }
        }
        console.debug("voice.tool_result", { name, callId, output })
        if (this._dc !== channel || channel?.readyState !== "open") return

        this._send({
            type: "conversation.item.create",
            item: {
                type: "function_call_output",
                call_id: callId,
                output: JSON.stringify(output)
            }
        })
        this._send({ type: "response.create" })
    }
}
